import { FC, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import { Select } from '../../../../types/redux/pages/PagesStore';
import { APPEND_NEW_SELECT_TO_TITLEBAR, REMOVE_SELECT_FROM_TITLEBAR } from '../../../../types/redux/pages/pageTypes';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import VariableMarketVolumeLiquidationCostsTables from './VariableMarketVolumeLiquidationCostTables.component';
import { Grid } from '@mui/material';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import type {
  LrstKeyOption,
  VariableMarketVolumeLiquidationCostsReport,
} from './variableMarketVolumeLiquidationCosts.types';
import { useVariableMarketVolumeLiquidationCostsPageModel } from '../../../../api/rs-hg/liquidity/hooks/queries';

const DEFAULT_SCENARIO_OPTIONS: LrstKeyOption[] = [
  {
    label: '5 x 4% Redemptions',
    value: 'redemption_liquidation_cost_4_5',
  },
  {
    label: '1 x 20% Redemptions',
    value: 'redemption_liquidation_cost_20_1',
  },
  {
    label: 'Weekly 20% Redemptions',
    value: 'redemption_liquidation_cost_20w_5',
  },
];

const VariableMarketVolumeLiquidationCosts: FC<FundInfoComponentProps> = (
  props,
) => {
  const activeDate = props.positionDate;

  const dispatch = useDispatch();

  const titleBarScenario = useTitleBarSelect('scenarioSelection');
  const selectedScenario =
    titleBarScenario ?? DEFAULT_SCENARIO_OPTIONS[0].value;

  const { data, isLoading, isFetching, isError, error } =
    useVariableMarketVolumeLiquidationCostsPageModel(props.fundId, activeDate);

  const reportData = data?.data;

  const lrstKeysSignature = useMemo(
    () => JSON.stringify(reportData?.lrst_keys ?? null),
    [reportData?.lrst_keys],
  );

  useEffect(() => {
    let scenarioSelectValues: LrstKeyOption[] = DEFAULT_SCENARIO_OPTIONS;
    if (lrstKeysSignature !== 'null') {
      try {
        const parsed = JSON.parse(lrstKeysSignature) as LrstKeyOption[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          scenarioSelectValues = parsed;
        }
      } catch {
        // keep defaults
      }
    }

    dispatch(toggleTitleBarLoading(true));
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Select Scenario',
        titleBarKey: 'scenarioSelection',
        values: scenarioSelectValues,
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: scenarioSelectValues[0].value,
      },
    ];
    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );
    dispatch(toggleTitleBarLoading(false));

    return () => {
      dispatch(
        removeTitleBarComponent(
          'scenarioSelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [lrstKeysSignature, dispatch]);

  return (
    <QueryBoundary<VariableMarketVolumeLiquidationCostsReport>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={reportData ?? undefined}
      loadingMessages={['Loading Market Liquidity Risk Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {(resolvedData) => (
        <Grid container>
          <VariableMarketVolumeLiquidationCostsTables
            data={resolvedData}
            selectedScenario={selectedScenario}
          />
        </Grid>
      )}
    </QueryBoundary>
  );
};

export default VariableMarketVolumeLiquidationCosts;
